/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable arrow-body-style */
import * as Yup from 'yup';
import { TEN_DIGITS_OBLIGATIONS, CONTACT_NUMBER_REGEX, NAME_FIELDS_NEW_REGEX, ORGANISATION_NAME_REGEX, FIELDS_WITH_SEPCIFI_CHARS, EMAIL_SPECIAL_CHARS_REGEX } from '@services/ValidationRules';
import { iSummary, iFormMetaData, iAddress, iContactMin } from '@interfaces/interfaces';
import { ProgressStep, FormType, nextDay } from '@constants';
import { FormikValues } from 'formik';
import pageValidationKeys from './PageValidationKeys';
import {
  EMPTY_DATE,
  INVALID_FUTURE_DATE,
  INVALID_PAST_DATE,
  INVALID_NUMERIC_FORMAT,
  INVALID_PHONE_NUMBER,
  INVALID_PHONE_NUMBER_LENGTH,
  INVALID_OBLIGATION_NUMBER,
  INVALID_OBLIGATION_TOTAL_NUMBER,
  INVALID_OBLIGATION_NUMBER_DUPLICATE,
  INVALID_EMAIL,
  MISSING_DECLARATION_AGREEMENT,
  FIELDS_WITH_SEPCIFI_CHARS_MSG,
  INVALID_ORG_NAME_FORMAT,
  INVALID_NAME_FORMAT_NO_SPECIAL,
  INVALID_PHONE_NUMBER_LENGTH_15,
} from './ErrorMessages';

const joinAddressFields = (entry: string[]) => {
  if (entry[0] === 'Address' || entry[0] === 'SearchText') return null;
  if (entry[0] === 'Country') return entry[1];
  return entry[1];
};

interface iValue {
  value: string;
}

export interface iCommonThirdPartyFormValues {
  [key: string]: string | number | boolean | iAddress | iValue[] | iContactMin | Date | null;
  authlastselection: string;
  // Step 1
  WhatDoesThisAuthorisationCover: string;
  // Step 2
  WhatFinesDoesThisAuthorisationCover: string;
  WhichFinesToProvideAuthorisationFor: string;
  AuthorisationForCurrentAndFutureFines: string;
  HowLongWillThisAuthorisationLast: string;
  PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeExcludedFromThisAuthority: string; // Fines not to be included
  PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeIncludedFromThisAuthority: string; // Fines to be included
  // The rest
  WhoAreYouNominating: string;
  GivenNames: string;
  FamilyName: string;
  EmailAddressOptional: string;
  EmailAddressOptionalWarning: string;
  OrganisationName: string;
  EmailAddressOptionalTwo: string;
  EmailAddressOptionalTwoWarning: string;
  YourReferenceNumberWithinTheOrganisationOptional: string;
  LastNameOrFamilyName: string;
  FirstOrGivenNames: string;
  DriverLicenceNumberOptional: string;
  StatecountryOfIssue: string;
  EmailAddressOptionalThree: string;
  EmailAddressOptionalThreeWarning: string;
  EMAIL_TO: string;
}

export interface iThirdPartyFormValues extends iCommonThirdPartyFormValues {
  obligations: iValue[];
  authexclude: string;
  authlast: Date | null;
  consentToDeclaration: boolean;
  IndividualContactNumber: iContactMin;
  IndividualContactNumberWarning: string;
  NoEmailAddress: boolean;
  OrganisationAddress: iAddress;
  OrganisationPostalAddress: iAddress;
  IsOrganisationAddressDifferentFromPostal: string;
  OrganisationContactNumber: iContactMin;
  OrganisationContactNumberWarning: string;
  NoEmailAddressTwo: boolean;
  DateOfBirth: Date | null;
  LicenceState: string;
  NoDriverLicence: boolean;
  ResidentialAddress: iAddress;
  ResidentialPostalAddress: iAddress;
  IsResidentialAddressSameAsPostal: string;
  UserContactNumber: iContactMin;
  UserContactNumberWarning: string;
  NoEmailAddressThree: boolean;
  submissionNo: string;
  submissionError: string;
  recaptchaResponse: string;
}

export interface iSubmitThirdPartyFormValues extends iCommonThirdPartyFormValues {
  ContactNumber: string;
  LineOne: string;
  LineTwo: string;
  Suburb: string;
  State: string;
  Postcode: string;
  Country: string;
  LineOneTwo: string;
  LineTwoTwo: string;
  SuburbTwo: string;
  StateTwo: string;
  PostcodeTwo: string;
  CountryTwo: string;
  ContactNumberTwo: string;
  DD: string;
  MM: string;
  YYYY: string;
  LineOneThree: string;
  LineTwoThree: string;
  SuburbThree: string;
  StateThree: string;
  PostcodeThree: string;
  CountryThree: string;
  LineOneFour: string;
  LineTwoFour: string;
  SuburbFour: string;
  StateFour: string;
  PostcodeFour: string;
  CountryFour: string;
  ContactNumberThree: string;
  YourSignature: string;
  DdTwo: number;
  MmTwo: number;
  YyyyTwo: number;
  FooterSubmissionNo1: string;
  FooterFormType1: string;
  FooterObligationNo1: string;
  FooterSubmittedTime1: string;
  FooterSubmissionNo2: string;
  FooterFormType2: string;
  FooterObligationNo2: string;
  FooterSubmittedTime2: string;
  FooterSubmissionNo3: string;
  FooterFormType3: string;
  FooterObligationNo3: string;
  FooterSubmittedTime3: string;
  FooterSubmissionNo4: string;
  FooterFormType4: string;
  FooterObligationNo4: string;
  FooterSubmittedTime4: string;
  FooterSubmissionNo5: string;
  FooterFormType5: string;
  FooterObligationNo5: string;
  FooterSubmittedTime5: string;
  recaptchaResponse: string;
}

export const defaultForm = (): iThirdPartyFormValues => {
  const defaultValues: iThirdPartyFormValues = {
    authlastselection: '',
    WhatDoesThisAuthorisationCover: '',
    WhatFinesDoesThisAuthorisationCover: '',
    HowLongWillThisAuthorisationLast: '',
    WhoAreYouNominating: '',
    FamilyName: '',
    GivenNames: '',
    EmailAddressOptional: '',
    EmailAddressOptionalWarning: '',
    OrganisationName: '',
    EmailAddressOptionalTwo: '',
    EmailAddressOptionalTwoWarning: '',
    YourReferenceNumberWithinTheOrganisationOptional: '',
    LastNameOrFamilyName: '',
    FirstOrGivenNames: '',
    DriverLicenceNumberOptional: '',
    StatecountryOfIssue: 'Australia',
    NoEmailAddressThree: false,
    EmailAddressOptionalThree: '',
    EmailAddressOptionalThreeWarning: '',
    EMAIL_TO: '',
    obligations: [{ value: '' }],
    authexclude: '',
    authlast: null,
    consentToDeclaration: false,
    IndividualContactNumber: {
      ContactNumber: '',
    },
    IndividualContactNumberWarning: '',
    NoEmailAddress: false,
    OrganisationAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    OrganisationPostalAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    IsOrganisationAddressDifferentFromPostal: '',
    OrganisationContactNumber: {
      ContactNumber: '',
    },
    OrganisationContactNumberWarning: '',
    NoEmailAddressTwo: false,
    DateOfBirth: null,
    LicenceState: '',
    NoDriverLicence: false,
    ResidentialAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    ResidentialPostalAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    IsResidentialAddressSameAsPostal: '',
    UserContactNumber: {
      ContactNumber: '',
    },
    UserContactNumberWarning: '',
    submissionNo: '',
    submissionError: '',
    WhichFinesToProvideAuthorisationFor: '',
    AuthorisationForCurrentAndFutureFines: '',
    PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeExcludedFromThisAuthority: '',
    PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeIncludedFromThisAuthority: '',
    recaptchaResponse: '',
  };
  return defaultValues;
};

export const getReceiptPayload = (): string => {
  return `{
      "ID": "",
      "Receipt": "",
      "Obligations": [
          {
              "ID": "",
              "ObligationNO": "0",
              "IsPrimary": true,
              "ReceptNumberId": ""
          }
      ],
      "SubmittedDate": "",
      "FormTypeId": 6
    }`;
};

// Payload is also used to generate pdf
export const getSubmissionPayload = (formValues: iThirdPartyFormValues, submissionNumber: string): iSubmitThirdPartyFormValues => {
  const submitFormValues = formValues;
  const now = new Date();
  const MONTH = now.getMonth() + 1;
  const MINUTE = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes();
  const SUBMITTED_TIME = `${now.getHours()} : ${MINUTE} ${now.getDate()}/${MONTH}/${now.getFullYear()}`;
  const FORM_TYPE = 'Thirdparty';

  if (formValues.NoEmailAddress) {
    submitFormValues.EmailAddressOptional = '';
  }

  if (formValues.NoEmailAddressTwo) {
    submitFormValues.EmailAddressOptionalTwo = '';
  }

  if (formValues.NoEmailAddressThree) {
    submitFormValues.EmailAddressOptionalThree = '';
  }
  if (formValues.NoDriverLicence) {
    submitFormValues.DriverLicenceNumberOptional = '';
    submitFormValues.StatecountryOfIssue = '';
    submitFormValues.LicenceState = '';
  }
  if (formValues.StatecountryOfIssue === 'Australia') {
    submitFormValues.StatecountryOfIssue = submitFormValues.LicenceState;
  }

  if (formValues.EMAIL_TO === 'true') {
    submitFormValues.EMAIL_TO = submitFormValues.EmailAddressOptionalThree;
  } else {
    submitFormValues.EMAIL_TO = '';
  }

  // Beginning of submitFormValues for Step 2
  submitFormValues.AuthorisationForCurrentAndFutureFines = formValues.WhatFinesDoesThisAuthorisationCover;
  if (formValues.WhatFinesDoesThisAuthorisationCover === 'No') {
    submitFormValues.WhichFinesToProvideAuthorisationFor = formValues.authexclude;
    if (Array.isArray(formValues.obligations)) {
      if (formValues.authexclude === 'All the following fines') {
        submitFormValues.PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeIncludedFromThisAuthority = formValues.obligations
          .map((obligation: any) => obligation.value)
          .join('; ');
        submitFormValues.PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeExcludedFromThisAuthority = '';
      } else {
        submitFormValues.PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeExcludedFromThisAuthority = formValues.obligations
          .map((obligation: any) => obligation.value)
          .join('; ');
        submitFormValues.PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeIncludedFromThisAuthority = '';
      }
    }
  } else {
    submitFormValues.PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeExcludedFromThisAuthority = '';
    submitFormValues.PleaseProvideObligationNumbersOfAnyFinesThatYouWishToBeIncludedFromThisAuthority = '';
    submitFormValues.WhichFinesToProvideAuthorisationFor = '';
    submitFormValues.WhatFinesDoesThisAuthorisationCover = '';
  }

  if (formValues.HowLongWillThisAuthorisationLast === 'Date') {
    const day = formValues.authlast!.getDate();
    const month = formValues.authlast!.getMonth() + 1;
    const year = formValues.authlast!.getFullYear();
    submitFormValues.HowLongWillThisAuthorisationLast = `${day < 10 ? `0${day}` : day}/${month < 10 ? `0${month}` : month}/${year}`;
  } else {
    submitFormValues.HowLongWillThisAuthorisationLast = formValues.HowLongWillThisAuthorisationLast;
  }
  // End of  submitFormValues for Step 2

  const submitObject: iSubmitThirdPartyFormValues = {
    ...(submitFormValues as iCommonThirdPartyFormValues),
    ContactNumber: formValues.IndividualContactNumber.ContactNumber,
    LineOne: formValues.OrganisationAddress.LineOne,
    LineTwo: formValues.OrganisationAddress.LineTwo,
    Suburb: formValues.OrganisationAddress.Suburb,
    State: formValues.OrganisationAddress.State,
    Postcode: formValues.OrganisationAddress.Postcode,
    Country: formValues.OrganisationAddress.State ? formValues.OrganisationAddress.Country : '',
    // Only populate postal address if it's explicitly set
    LineOneTwo: formValues.IsOrganisationAddressDifferentFromPostal === 'true' ? formValues.OrganisationPostalAddress.LineOne : '',
    LineTwoTwo: formValues.IsOrganisationAddressDifferentFromPostal === 'true' ? formValues.OrganisationPostalAddress.LineTwo : '',
    SuburbTwo: formValues.IsOrganisationAddressDifferentFromPostal === 'true' ? formValues.OrganisationPostalAddress.Suburb : '',
    StateTwo: formValues.IsOrganisationAddressDifferentFromPostal === 'true' ? formValues.OrganisationPostalAddress.State : '',
    PostcodeTwo: formValues.IsOrganisationAddressDifferentFromPostal === 'true' ? formValues.OrganisationPostalAddress.Postcode : '',
    CountryTwo: formValues.IsOrganisationAddressDifferentFromPostal === 'true' ? formValues.OrganisationPostalAddress.Country : '',
    ContactNumberTwo: formValues.OrganisationContactNumber.ContactNumber,
    DD: formValues.DateOfBirth ? formValues.DateOfBirth.getDate().toString() : '',
    MM: formValues.DateOfBirth ? (formValues.DateOfBirth.getMonth() + 1).toString() : '',
    YYYY: formValues.DateOfBirth ? formValues.DateOfBirth.getFullYear().toString() : '',
    LineOneThree: formValues.ResidentialAddress.LineOne,
    LineTwoThree: formValues.ResidentialAddress.LineTwo,
    SuburbThree: formValues.ResidentialAddress.Suburb,
    StateThree: formValues.ResidentialAddress.State,
    PostcodeThree: formValues.ResidentialAddress.Postcode,
    CountryThree: formValues.ResidentialAddress.State ? formValues.ResidentialAddress.Country : '',
    LineOneFour: formValues.IsResidentialAddressSameAsPostal === 'true' ? formValues.ResidentialPostalAddress.LineOne : formValues.ResidentialAddress.LineOne,
    LineTwoFour: formValues.IsResidentialAddressSameAsPostal === 'true' ? formValues.ResidentialPostalAddress.LineTwo : formValues.ResidentialAddress.LineTwo,
    SuburbFour: formValues.IsResidentialAddressSameAsPostal === 'true' ? formValues.ResidentialPostalAddress.Suburb : formValues.ResidentialAddress.Suburb,
    StateFour: formValues.IsResidentialAddressSameAsPostal === 'true' ? formValues.ResidentialPostalAddress.State : formValues.ResidentialAddress.State,
    PostcodeFour: formValues.IsResidentialAddressSameAsPostal === 'true' ? formValues.ResidentialPostalAddress.Postcode : formValues.ResidentialAddress.Postcode,
    CountryFour: formValues.IsResidentialAddressSameAsPostal === 'true' ? formValues.ResidentialPostalAddress.Country : formValues.ResidentialAddress.Country,
    ContactNumberThree: formValues.UserContactNumber.ContactNumber,
    YourSignature: formValues.consentToDeclaration ? 'I consent to this Authorisation' : '',
    DdTwo: now.getDate(),
    MmTwo: MONTH,
    YyyyTwo: now.getFullYear(),
    FooterSubmissionNo1: submissionNumber,
    FooterFormType1: FORM_TYPE,
    FooterObligationNo1: '',
    FooterSubmittedTime1: SUBMITTED_TIME,
    FooterSubmissionNo2: submissionNumber,
    FooterFormType2: FORM_TYPE,
    FooterObligationNo2: '',
    FooterSubmittedTime2: SUBMITTED_TIME,
    FooterSubmissionNo3: submissionNumber,
    FooterFormType3: FORM_TYPE,
    FooterObligationNo3: '',
    FooterSubmittedTime3: SUBMITTED_TIME,
    FooterSubmissionNo4: submissionNumber,
    FooterFormType4: FORM_TYPE,
    FooterObligationNo4: '',
    FooterSubmittedTime4: SUBMITTED_TIME,
    FooterSubmissionNo5: submissionNumber,
    FooterFormType5: FORM_TYPE,
    FooterObligationNo5: '',
    FooterSubmittedTime5: SUBMITTED_TIME,
    recaptchaResponse: formValues.recaptchaResponse,
  };

  const excludeKeysFromForm = [
    'obligations',
    'authexclude',
    'authlast',
    'consentToDeclaration',
    'IndividualContactNumber',
    'NoEmailAddress',
    'OrganisationAddress',
    'OrganisationPostalAddress',
    'IsOrganisationAddressDifferentFromPostal',
    'OrganisationContactNumber',
    'NoEmailAddressTwo',
    'DateOfBirth',
    'LicenceState',
    'NoDriverLicence',
    'ResidentialAddress',
    'ResidentialPostalAddress',
    'IsResidentialAddressSameAsPostal',
    'UserContactNumber',
    'NoEmailAddressThree',
    'submissionError',
    'EmailAddressOptionalWarning',
    'EmailAddressOptionalTwoWarning',
    'IndividualContactNumberWarning',
    'OrganisationContactNumberWarning',
    'EmailAddressOptionalThreeWarning',
    'UserContactNumberWarning',
  ];
  excludeKeysFromForm.forEach((value: string) => {
    delete submitObject[value];
  });
  return submitObject;
};

export const ThirdPartySchema = Yup.object().shape({
  obligations: Yup.array()
    .test('unique', INVALID_OBLIGATION_NUMBER_DUPLICATE, (obligations: any) => {
      const uniqueObligation = new Set();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < obligations.length; i++) {
        const { value } = obligations[i];

        if (uniqueObligation.has(value)) {
          return false;
        }

        uniqueObligation.add(value);
      }
      return true;
    })
    .when('WhatFinesDoesThisAuthorisationCover', {
      is: (value: string) => value === 'No',
      then: Yup.array()
        .min(1)
        .max(25, INVALID_OBLIGATION_TOTAL_NUMBER)
        .of(
          Yup.object().shape({
            value: Yup.string()
              .length(10, INVALID_OBLIGATION_NUMBER)
              .min(1, INVALID_OBLIGATION_NUMBER)
              .trim()
              .matches(TEN_DIGITS_OBLIGATIONS, INVALID_OBLIGATION_NUMBER)
              .required(INVALID_NUMERIC_FORMAT),
          }),
        ),
    }),
  OrganisationAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  OrganisationPostalAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  ResidentialAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  ResidentialPostalAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  DateOfBirth: Yup.date().required(EMPTY_DATE).nullable().max(new Date(), INVALID_FUTURE_DATE),
  IndividualContactNumber: Yup.object().shape({
    ContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
  }),
  OrganisationContactNumber: Yup.object().shape({
    ContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
  }),
  UserContactNumber: Yup.object().shape({
    ContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
  }),
  GivenNames: Yup.string().max(80).matches(NAME_FIELDS_NEW_REGEX, INVALID_NAME_FORMAT_NO_SPECIAL).required(INVALID_NUMERIC_FORMAT),
  FamilyName: Yup.string().max(80).matches(NAME_FIELDS_NEW_REGEX, INVALID_NAME_FORMAT_NO_SPECIAL).required(INVALID_NUMERIC_FORMAT),
  WhatDoesThisAuthorisationCover: Yup.string().required(INVALID_NUMERIC_FORMAT),
  WhatFinesDoesThisAuthorisationCover: Yup.string().required(INVALID_NUMERIC_FORMAT),
  HowLongWillThisAuthorisationLast: Yup.string().required(INVALID_NUMERIC_FORMAT),
  WhoAreYouNominating: Yup.string().required(INVALID_NUMERIC_FORMAT),
  authexclude: Yup.string().when('WhatFinesDoesThisAuthorisationCover', {
    is: (value: string) => value === 'No',
    then: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  consentToDeclaration: Yup.boolean().oneOf([true], MISSING_DECLARATION_AGREEMENT),
  EmailAddressOptional: Yup.string().matches(EMAIL_SPECIAL_CHARS_REGEX, INVALID_EMAIL).email(INVALID_EMAIL).required(INVALID_NUMERIC_FORMAT),
  NoEmailAddress: Yup.boolean(),
  OrganisationName: Yup.string().max(80).matches(ORGANISATION_NAME_REGEX, INVALID_ORG_NAME_FORMAT).required(INVALID_NUMERIC_FORMAT),
  EmailAddressOptionalTwo: Yup.string().matches(EMAIL_SPECIAL_CHARS_REGEX, INVALID_EMAIL).email(INVALID_EMAIL).required(INVALID_NUMERIC_FORMAT),
  NoEmailAddressTwo: Yup.boolean(),
  YourReferenceNumberWithinTheOrganisationOptional: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
  LastNameOrFamilyName: Yup.string().max(80).matches(NAME_FIELDS_NEW_REGEX, INVALID_NAME_FORMAT_NO_SPECIAL).required(INVALID_NUMERIC_FORMAT),
  FirstOrGivenNames: Yup.string().max(80).matches(NAME_FIELDS_NEW_REGEX, INVALID_NAME_FORMAT_NO_SPECIAL).required(INVALID_NUMERIC_FORMAT),
  DriverLicenceNumberOptional: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
  StatecountryOfIssue: Yup.string(),
  LicenceState: Yup.string().required(INVALID_NUMERIC_FORMAT),
  NoDriverLicence: Yup.boolean(),
  EmailAddressOptionalThree: Yup.string().matches(EMAIL_SPECIAL_CHARS_REGEX, INVALID_EMAIL).email(INVALID_EMAIL).required(INVALID_NUMERIC_FORMAT),
  NoEmailAddressThree: Yup.boolean(),
  IsOrganisationAddressDifferentFromPostal: Yup.string().required(INVALID_NUMERIC_FORMAT),
  IsResidentialAddressSameAsPostal: Yup.string().required(INVALID_NUMERIC_FORMAT),
  authlast: Yup.date().when('HowLongWillThisAuthorisationLast', {
    is: (value: string) => value === 'Date',
    then: Yup.date().required(EMPTY_DATE).nullable().min(nextDay, INVALID_PAST_DATE),
    otherwise: Yup.date(),
  }),
  EMAIL_TO: Yup.string().required(INVALID_NUMERIC_FORMAT),
});

const { THIRDPARTY } = FormType;

export const thirdPartyMetaData: iFormMetaData = {
  pagesSegments: {
    'Third-Party': {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => pageValidationKeys(values, THIRDPARTY).page1,
    },
    step1: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => pageValidationKeys(values, THIRDPARTY).page1,
    },
    step2: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => pageValidationKeys(values, THIRDPARTY).page2,
    },
    step3: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => pageValidationKeys(values, THIRDPARTY).page3,
    },
    step4: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => pageValidationKeys(values, THIRDPARTY).page4,
    },
    step5: {
      progressStep: ProgressStep.CHECK_CONFIRM,
      validationKeys: (values: FormikValues) => [],
    },
    step6: {
      progressStep: ProgressStep.CHECK_CONFIRM,
      validationKeys: (values: FormikValues) => pageValidationKeys(values, THIRDPARTY).page6,
    },
    step7: {
      progressStep: ProgressStep.DONE,
      validationKeys: (values: FormikValues) => [],
    },
  },
};

export const getSummaryScreen = (values: iThirdPartyFormValues): iSummary[] => {
  const page1 = {
    title: 'What type of authorisation would you like',
    editLink: '/step1',
    fields: [[{ title: 'What type of authorisation would you like?', value: values.WhatDoesThisAuthorisationCover }]],
  };

  const page2 = {
    title: 'What are you authorising',
    editLink: '/step2',
    fields: [[{ title: 'Would you like to provide authorisation for all current and future fines?', value: values.WhatFinesDoesThisAuthorisationCover }]],
  };

  const authlastUntilDate = values.authlast !== null ? `Until the date: ${values.authlast.getDate()}/${values.authlast.getMonth() + 1}/${values.authlast.getFullYear()}` : 'n/a';

  const addFinesToSummary = (obligations: iValue[]) => {
    obligations.forEach((e, index) => {
      page2.fields.push([{ title: `Obligation ${index + 1}`, value: e.value }]);
    });
  };

  if (values.WhatFinesDoesThisAuthorisationCover === 'No') {
    const WhatFinesDoesThisAuthorisationCoverTitle = {
      title: 'Which fines would you like to provide authorisation for?',
      value: values.authexclude,
    };

    page2.fields.push([WhatFinesDoesThisAuthorisationCoverTitle]);
    addFinesToSummary(values.obligations);
  }

  page2.fields.push([
    {
      title: 'How long would you like this authorisation to last?',
      value: values.HowLongWillThisAuthorisationLast === 'Date' ? authlastUntilDate : values.HowLongWillThisAuthorisationLast,
    },
  ]);

  const page3 = {
    title: 'Who are you nominating to enquire or act on your behalf',
    editLink: '/step3',
    fields:
      values.WhoAreYouNominating === 'individual'
        ? [
            [{ title: 'Given name(s)', value: values.GivenNames }],
            [{ title: 'Family name', value: values.FamilyName }],
            [{ title: 'Contact phone number', value: values.IndividualContactNumber.ContactNumber }],
            [{ title: 'Email', value: values.EmailAddressOptional ? values.EmailAddressOptional : 'n/a' }],
          ]
        : [
            [{ title: 'Organisation name', value: values.OrganisationName }],
            [
              {
                title: "Organisation's address",
                value: `${Object.entries(values.OrganisationAddress)
                  .map((entry) => joinAddressFields(entry))
                  .filter(Boolean)
                  .join(', ')}`,
              },
            ],
            values.IsOrganisationAddressDifferentFromPostal === 'false'
              ? [{ title: "Is your postal address different to your organisation's address?", value: 'No' }]
              : [
                  {
                    title: 'Postal address',
                    value: `${Object.entries(values.OrganisationPostalAddress)
                      .map((entry) => joinAddressFields(entry))
                      .filter(Boolean)
                      .join(', ')}`,
                  },
                ],
            [{ title: 'Contact phone number', value: values.OrganisationContactNumber.ContactNumber }],
            [{ title: 'Email', value: values.EmailAddressOptionalTwo ? values.EmailAddressOptionalTwo : 'n/a' }],
            [
              {
                title: 'Your reference number within the organisation',
                value: values.YourReferenceNumberWithinTheOrganisationOptional ? values.YourReferenceNumberWithinTheOrganisationOptional : 'n/a',
              },
            ],
          ],
  };

  const page4 = {
    title: 'Your details',
    editLink: '/step4',
    fields: [
      [{ title: 'Given name(s)', value: values.FirstOrGivenNames }],
      [{ title: 'Family name', value: values.LastNameOrFamilyName }],
      [
        {
          title: 'Date of birth',
          value: `${values.DateOfBirth ? `${values.DateOfBirth.getDate()}/${values.DateOfBirth.getMonth() + 1}/${values.DateOfBirth.getFullYear()}` : ''}`,
        },
      ],
      values.NoDriverLicence ? [{ title: 'Driver licence', value: 'n/a' }] : [{ title: 'Driver licence', value: values.DriverLicenceNumberOptional }],
      values.NoDriverLicence
        ? [{ title: 'Place of issue', value: 'n/a' }]
        : [{ title: 'Place of issue', value: values.StatecountryOfIssue === 'Australia' ? values.LicenceState : values.StatecountryOfIssue }],
      [
        {
          title: 'Residential address',
          value: `${Object.entries(values.ResidentialAddress)
            .map((entry) => joinAddressFields(entry))
            .filter(Boolean)
            .join(', ')}`,
        },
      ],
      values.IsResidentialAddressSameAsPostal === 'false'
        ? [{ title: 'Is your postal address different to your residential address?', value: 'No' }]
        : [
            {
              title: 'Postal address',
              value: `${Object.entries(values.ResidentialPostalAddress)
                .map((entry) => joinAddressFields(entry))
                .filter(Boolean)
                .join(', ')}`,
            },
          ],
      [{ title: 'Contact phone number', value: values.UserContactNumber.ContactNumber }],
      [{ title: 'Email', value: values.EmailAddressOptionalThree ? values.EmailAddressOptionalThree : 'n/a' }],
      [{ title: 'Would you like to receive a copy of the completed application at the email address provided?', value: values.EMAIL_TO === 'true' ? 'Yes' : 'No' }],
    ],
  };

  const summarySections: iSummary[] = [page1, page2, page3, page4];
  return summarySections;
};
